import { createFileRoute, notFound } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { BlogPostPage } from "@/components/calv/blog/BlogPostPage";
import { getPost, getAlternateSlug, type BlogPost } from "@/lib/blog";
import { SITE_URL } from "@/lib/locale";

type LoaderData = { post: BlogPost | null; alternateLocalePath: string };

export const Route = createFileRoute("/blog/$slug")({
  // Loader is async — MD body, marked + js-yaml are dynamic-imported.
  // Topbar reads `alternateLocalePath` from loaderData, so the sitewide
  // chrome never imports the markdown pipeline.
  loader: async ({ params }): Promise<LoaderData> => {
    const post = await getPost(params.slug, "de");
    if (!post) return { post: null, alternateLocalePath: "/en/blog" };
    const alt = getAlternateSlug(post.dirKey, "en");
    return { post, alternateLocalePath: alt ? `/en/blog/${alt}` : "/en/blog" };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      const h = pageHead("blog", "de");
      return { meta: h.meta, links: h.links };
    }
    const enSlug = getAlternateSlug(post.dirKey, "en") ?? post.fm.slug;
    const canonical = `${SITE_URL}/blog/${post.fm.slug}`;
    const altEn = `${SITE_URL}/en/blog/${enSlug}`;
    return {
      meta: [
        { title: post.fm.seoTitle },
        { name: "description", content: post.fm.description },
        { property: "og:title", content: post.fm.seoTitle },
        { property: "og:description", content: post.fm.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "de_DE" },
        { property: "og:locale:alternate", content: "en_US" },
        { property: "og:image", content: `${SITE_URL}${post.fm.ogImage ?? "/og/default-1200x630.png"}` },
        { property: "article:published_time", content: post.fm.datePublished },
        { property: "article:modified_time", content: post.fm.dateModified },
        { property: "article:author", content: "Calvarius GmbH" },
        { property: "article:section", content: post.fm.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.fm.seoTitle },
        { name: "twitter:description", content: post.fm.description },
        { name: "twitter:image", content: `${SITE_URL}${post.fm.ogImage ?? "/og/default-1200x630.png"}` },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "de", href: canonical },
        { rel: "alternate", hrefLang: "en", href: altEn },
        { rel: "alternate", hrefLang: "x-default", href: canonical },
      ],
    };
  },
  component: PostRoute,
  notFoundComponent: () => <div className="p-12">Beitrag nicht gefunden.</div>,
});

function PostRoute() {
  const { post } = Route.useLoaderData();
  if (!post) throw notFound();
  return <BlogPostPage post={post} />;
}
